import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';

/**
 * Download an image from a URL and upload it to Firebase Storage
 * Follows the same pattern as RecipeFormV2: recipe-images/{folderId}/{timestamp}-{name}
 * @param imageUrl - The source image URL (e.g. TikTok CDN)
 * @param recipeName - Recipe name for generating a meaningful filename
 * @param folderId - Folder ID for organizing files (organizationId or userId)
 * @returns Firebase Storage download URL
 */
export async function downloadAndUploadImage(
  imageUrl: string,
  recipeName: string,
  folderId: string
): Promise<string> {
  try {
    console.log('📥 Downloading image from:', imageUrl);

    // Download image from source URL with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

    const response = await fetch(imageUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.statusText}`);
    }

    const blob = await response.blob();
    console.log('✅ Image downloaded, size:', (blob.size / 1024).toFixed(2), 'KB');

    // Generate a unique filename following the same pattern as RecipeFormV2
    const timestamp = Date.now();
    const sanitizedName = recipeName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with dashes
      .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
      .substring(0, 50); // Limit length

    const extension = blob.type.split('/')[1] || 'jpg';
    // Use the same path structure as RecipeFormV2: recipe-images/{folderId}/{timestamp}-{name}
    // folderId can be organizationId (preferred) or userId (fallback)
    const filename = `recipe-images/${folderId}/${timestamp}-${sanitizedName}.${extension}`;

    // Upload to Firebase Storage with resumable upload
    const storageRef = ref(storage, filename);
    console.log('📤 Uploading to Firebase Storage:', filename);
    
    const uploadTask = uploadBytesResumable(storageRef, blob, {
      contentType: blob.type,
      cacheControl: 'public, max-age=31536000', // Cache for 1 year
    });

    // Return a promise that resolves with the download URL when complete
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${progress.toFixed(1)}%`);
        },
        (error) => {
          console.error('❌ Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('✅ Image uploaded successfully:', downloadURL);
            resolve(downloadURL);
          } catch (error) {
            console.error('❌ Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('❌ Error downloading/uploading image:', error);
    throw error;
  }
}

/**
 * Check if a URL is a TikTok CDN URL
 */
export function isTikTokImageUrl(url: string): boolean {
  return url.includes('tiktokcdn') || url.includes('tiktok.com');
}

